import { useEffect, useRef, useState } from "react";
import { Camera, RefreshCw, X } from "lucide-react";
import { extractVin } from "../lib/vinOcr";

export default function VinScannerModal({ onResult, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" }, audio: false })
      .then((stream) => {
        if (!active) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => setError("Kameraya erişilemedi. Lütfen tarayıcı kamera iznini verin."));
    return () => {
      active = false;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const scan = async () => {
    const video = videoRef.current;
    if (!video || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext("2d").drawImage(video, 0, 0);
      const { createWorker } = await import("tesseract.js");
      const worker = await createWorker("eng");
      await worker.setParameters({ tessedit_char_whitelist: "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789" });
      const { data } = await worker.recognize(canvas);
      await worker.terminate();
      const vin = extractVin(data.text);
      if (vin) {
        onResult(vin);
        onClose();
      } else {
        setFailed(true);
      }
    } catch {
      setFailed(true);
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="vin-scanner-modal">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-[#0B1B33]">
          <p className="text-sm font-bold on-img flex items-center gap-2"><Camera className="w-4 h-4" /> Şasi / VIN Tara</p>
          <button type="button" onClick={onClose} data-testid="vin-scanner-close" className="p-1" aria-label="Kapat">
            <X className="w-5 h-5 on-img" />
          </button>
        </div>
        <div className="p-4">
          <p className="text-xs text-slate-500 mb-3">Ruhsattaki, kapı direğindeki veya araç üzerindeki Şasi/VIN etiketini kameraya gösterin.</p>
          <div className="rounded-xl overflow-hidden bg-black aspect-video">
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="vin-scanner-video" />
          </div>
          {error && <p className="mt-3 text-sm text-red-600" data-testid="vin-scanner-error">{error}</p>}
          {failed && !error && (
            <p className="mt-3 text-sm text-red-600" data-testid="vin-scanner-failed">VIN okunamadı. Etiketi yaklaştırıp tekrar deneyin.</p>
          )}
          <div className="mt-4 flex gap-3">
            <button type="button" onClick={scan} disabled={busy || !!error} data-testid="vin-scanner-capture"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-orange-500 text-white font-bold hover:bg-orange-400 disabled:opacity-60">
              <Camera className="w-5 h-5" /> {busy ? "Okunuyor..." : failed ? "Tekrar Tara" : "Tara"}
            </button>
            {failed && (
              <button type="button" onClick={scan} disabled={busy} data-testid="vin-scanner-retry"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-slate-300 text-slate-600 font-bold">
                <RefreshCw className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
